import { useState, useEffect, useCallback } from 'react'
import { Project } from './types'
import { readFromSupabase, writeToSupabase } from './supabase'

const defaultProjects: Project[] = [
  {
    id: '1',
    title: 'Fintech Trading Platform',
    num: '01',
    year: '2025',
    subtitle: 'Next-gen crypto trading',
    summary:
      'Designed a crypto trading platform focused on speed, clarity, and mobile-first experience.',
    role: 'Product Designer',
    tools: ['Figma', 'React', 'Tailwind'],
    category: 'Fintech',
    overview: 'A comprehensive redesign of a major crypto trading platform.',
    challenge: 'Users found the existing interface cluttered and intimidating.',
    approach:
      'Simplified the core trading loop and introduced progressive disclosure.',
    outcome: 'Increased daily active users by 40% and reduced support tickets.',
    metrics: ['40% increase in DAU', '2.5x faster trade execution'],
    lessonsLearned: 'Simplicity is key in complex financial interfaces.',
    nextSteps: 'Implement advanced charting tools.',
    ctaText: 'View Live Site',
    ctaLink: '#',
    liveUrl: '#',
    prototypeUrl: '',
    slug: 'fintech-trading-platform',
    status: 'published',
    featured: true,
    seoTitle: 'Fintech Trading Platform | Case Study',
    seoDescription: 'Case study for a next-gen crypto trading platform.',
    ogImage: '',
    coverImage:
      'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&q=80',
    heroStyle: 'default',
    accentColor: 'hsl(72 100% 50%)',
    backgroundColor: 'hsl(0 0% 6%)',
    textColor: 'hsl(0 0% 92%)',
    sectionSpacing: 'medium',
    customCSS: '',
    blocks: [
      {
        id: 'b1',
        type: 'hero',
        content: {
          title: 'Fintech Trading Platform',
          subtitle:
            'Redesigning the future of crypto trading for modern investors.',
          backgroundImage:
            'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&q=80',
          overlayOpacity: 70,
          alignment: 'center',
          ctaText: 'View Live Prototype',
          ctaLink: '#',
        },
        settings: {
          paddingTop: 'none',
          paddingBottom: 'large',
          maxWidth: 'full',
        },
      },
      {
        id: 'b2',
        type: 'rich-text',
        content: {
          html: '<h2>The Challenge</h2><p>Modern crypto trading platforms suffer from feature bloat. New users are overwhelmed by complex charts, order books, and technical jargon, while experienced traders find the interfaces clunky and slow. Our goal was to bridge this gap by creating a platform that is approachable for beginners but powerful enough for pros.</p>',
        },
        settings: {
          paddingTop: 'medium',
          paddingBottom: 'medium',
          maxWidth: 'narrow',
        },
      },
      {
        id: 'b3',
        type: 'two-column',
        content: {
          leftContent:
            '<h3>Progressive Disclosure</h3><p>We implemented a tiered interface system. The default view shows a clean, simple swap interface similar to consumer apps. With a single toggle, users can activate <i>Pro Mode</i> which reveals advanced charting, order books, and depth charts without leaving the context of their current trade.</p>',
          imageUrl:
            'https://images.unsplash.com/photo-1642543492481-44e81e391452?w=1200&q=80',
          imageAlt: 'Dashboard Interface',
          ratio: '50-50',
          reverseMobile: false,
        },
        settings: {
          paddingTop: 'medium',
          paddingBottom: 'medium',
          maxWidth: 'default',
        },
      },
      {
        id: 'b4',
        type: 'metrics',
        content: {
          columns: '3',
          metrics: [
            {
              value: '40%',
              label: 'Increase in DAU',
              description: 'Sustained growth over 6 months',
            },
            {
              value: '2.5x',
              label: 'Faster Execution',
              description: 'Reduced time to complete a trade',
            },
            {
              value: '-60%',
              label: 'Support Tickets',
              description: 'Fewer onboarding-related questions',
            },
          ],
        },
        settings: {
          paddingTop: 'large',
          paddingBottom: 'large',
          backgroundColor: 'hsl(0 0% 9%)',
          maxWidth: 'default',
        },
      },
      {
        id: 'b5',
        type: 'gallery',
        content: {
          columns: '2',
          gap: 'medium',
          images: [
            {
              url: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80',
              alt: 'Mobile App',
              caption: 'Mobile trading interface',
            },
            {
              url: 'https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?w=1200&q=80',
              alt: 'Analytics',
              caption: 'Portfolio analytics dashboard',
            },
          ],
        },
        settings: {
          paddingTop: 'medium',
          paddingBottom: 'large',
          maxWidth: 'default',
        },
      },
      {
        id: 'b6',
        type: 'testimonial',
        content: {
          quote:
            'The new design completely transformed how our users interact with the platform. It feels incredibly fast and intuitive.',
          authorName: 'Sarah Jenkins',
          authorRole: 'VP of Product',
          variant: 'large',
        },
        settings: {
          paddingTop: 'medium',
          paddingBottom: 'large',
          maxWidth: 'default',
        },
      },
    ],
    createdAt: '2025-01-15T10:00:00.000Z',
    updatedAt: '2025-03-10T14:30:00.000Z',
  },
  {
    id: '2',
    title: 'AI Music Tool',
    num: '02',
    year: '2024',
    subtitle: 'AI-powered music generation for creators',
    summary: 'An AI-powered music generation tool for producers and artists.',
    role: 'Product Designer / Creative Developer',
    tools: ['Figma', 'Web Audio API', 'AI tools'],
    category: 'Creative Tech',
    overview:
      'Designed the UX for an AI music generation platform that makes music creation accessible to everyone.',
    challenge:
      'Making AI music creation intuitive for non-technical musicians.',
    approach:
      'Built a visual workflow that abstracts AI complexity behind familiar music production metaphors.',
    outcome: 'Launched to 10,000+ beta users with 85% retention rate.',
    metrics: ['10K+ beta users', '85% retention rate', '4.9★ avg rating'],
    lessonsLearned:
      'Creative tools need to feel like instruments, not software.',
    nextSteps: 'Add collaborative features and real-time jamming.',
    ctaText: 'Try the Demo',
    ctaLink: '#',
    liveUrl: '#',
    prototypeUrl: '',
    slug: 'ai-music-tool',
    status: 'published',
    featured: false,
    seoTitle: 'AI Music Tool | Case Study',
    seoDescription: 'Case study for an AI music generation platform.',
    ogImage: '',
    coverImage:
      'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=1200&q=80',
    heroStyle: 'centered',
    accentColor: '#a855f7',
    backgroundColor: 'hsl(0 0% 6%)',
    textColor: 'hsl(0 0% 92%)',
    sectionSpacing: 'large',
    customCSS: '',
    blocks: [
      {
        id: 'b7',
        type: 'hero',
        content: {
          title: 'AI Music Tool',
          subtitle: 'Creating music with artificial intelligence',
          backgroundImage:
            'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=1200&q=80',
          overlayOpacity: 70,
          alignment: 'center',
          ctaText: '',
          ctaLink: '',
        },
        settings: {
          paddingTop: 'none',
          paddingBottom: 'large',
          maxWidth: 'full',
        },
      },
      {
        id: 'b8',
        type: 'rich-text',
        content: {
          html: '<h2>The Vision</h2><p>We set out to democratize music creation by building an AI-powered tool that feels intuitive to musicians and non-musicians alike. The platform needed to balance creative control with AI assistance.</p>',
        },
        settings: {
          paddingTop: 'medium',
          paddingBottom: 'medium',
          maxWidth: 'narrow',
        },
      },
      {
        id: 'b9',
        type: 'timeline',
        content: {
          steps: [
            {
              title: 'User Research',
              description:
                'Interviewed 50+ musicians across skill levels to understand pain points.',
              label: '01',
            },
            {
              title: 'Concept Design',
              description:
                'Created a visual node-based workflow inspired by modular synthesizers.',
              label: '02',
            },
            {
              title: 'Prototyping',
              description:
                'Built interactive prototypes with real audio generation.',
              label: '03',
            },
            {
              title: 'Beta Launch',
              description:
                'Launched to 10K users and iterated based on feedback.',
              label: '04',
            },
          ],
          layout: 'vertical',
        },
        settings: {
          paddingTop: 'large',
          paddingBottom: 'large',
          maxWidth: 'default',
        },
      },
      {
        id: 'b10',
        type: 'metrics',
        content: {
          columns: '3',
          metrics: [
            {
              value: '10K+',
              label: 'Beta Users',
              description: 'Organic growth in first month',
            },
            {
              value: '85%',
              label: 'Retention',
              description: '30-day retention rate',
            },
            {
              value: '4.9★',
              label: 'Rating',
              description: 'Average user satisfaction',
            },
          ],
        },
        settings: {
          paddingTop: 'medium',
          paddingBottom: 'large',
          maxWidth: 'default',
        },
      },
    ],
    createdAt: '2024-11-01T10:00:00.000Z',
    updatedAt: '2025-02-20T09:15:00.000Z',
  },
]

// Simple event bus for same-tab sync (mirrors cmsStore pattern)
const projectListeners: Set<(data: Project[]) => void> = new Set()

function notifyProjectListeners(data: Project[]) {
  projectListeners.forEach((fn) => fn(data))
}

export function useProjects() {
  const [projects, setProjects] = useState<Project[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function fetchFromSupabase() {
      const remote = await readFromSupabase<Project[]>('sinka_projects')
      if (!cancelled) {
        if (remote !== null) {
          setProjects(remote)
          notifyProjectListeners(remote)
        } else {
          // Seed Supabase with default data
          await writeToSupabase('sinka_projects', defaultProjects)
          setProjects(defaultProjects)
          notifyProjectListeners(defaultProjects)
        }
        setIsLoading(false)
      }
    }
    fetchFromSupabase()
    return () => {
      cancelled = true
    }
  }, [])

  // Listen for updates from other hook instances (same tab)
  useEffect(() => {
    const listener = (newData: Project[]) => setProjects(newData)
    projectListeners.add(listener)
    return () => {
      projectListeners.delete(listener)
    }
  }, [])

  const saveProject = useCallback((project: Project) => {
    setProjects((prev) => {
      const index = prev.findIndex((p) => p.id === project.id)
      const updated = [...prev]
      if (index >= 0) {
        updated[index] = project
      } else {
        updated.push(project)
      }
      import('./versionHistory').then(({ saveSnapshot }) => {
        saveSnapshot('sinka_projects', prev, 'Auto-save')
      })
      writeToSupabase('sinka_projects', updated)
      notifyProjectListeners(updated)
      return updated
    })
  }, [])

  const deleteProject = useCallback((id: string) => {
    setProjects((prev) => {
      const updated = prev.filter((p) => p.id !== id)
      import('./versionHistory').then(({ saveSnapshot }) => {
        saveSnapshot('sinka_projects', prev, 'Before delete')
      })
      writeToSupabase('sinka_projects', updated)
      notifyProjectListeners(updated)
      return updated
    })
  }, [])

  const reorderProjects = useCallback((newProjects: Project[]) => {
    setProjects(newProjects)
    writeToSupabase('sinka_projects', newProjects)
    notifyProjectListeners(newProjects)
  }, [])

  const getProject = useCallback(
    (id: string) => {
      return projects.find((p) => p.id === id)
    },
    [projects],
  )

  return {
    projects,
    isLoading,
    saveProject,
    deleteProject,
    reorderProjects,
    getProject,
  }
}
;('>')
